import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { AttendeePresentation } from '../_attendee-presentation/attendee-presentation';
import { AttendeeAdditionalInfo } from '../_attendee-presentation/attendee-additional-info';
import AttendeeNotesSection from '../../_attendee-notes-section/_attendee-notes-section';
import { Popover } from '../../../../../../global/popover/popover';
import { IAttendeeNote, IControlAttendeeDetailsDTO, MeetingAttendeeIssue, MeetingAttendeeStatus } from '../../../../../../../api/attendees.dao';
import OEMultiSelectItem from '../../../../../../global/oe-multi-select/_oe-multi-select-item/_oe-multi-select-item';
import { IAttendeeAdditionalInfo } from '../_attendee';
import ChevronSVG from '../../../../../../../images/svg/icons8-expand-arrow';
import DefaultButton from '../../../../../../global/default-button/default-button';

import './info-button.scss';
import Text from '../../../../../../global/text/text';
import { REMOVE_TEXT } from '../../../../../../../constants/common-labels.constants';

interface IProps {
    firstName: string;
    lastName: string;
    markedName?: string | JSX.Element;
    markedOrganization?: string | JSX.Element;
    handleToggleLate: () => void;
    isLate: boolean;
    isAttendeeJoined: boolean;
    attendeeId: number;
    openEditAttendeeSubWindow: () => void;
    eventTimezone: string;
    withTableView: boolean;
    fetchAttendeeData: (attendeeId: number) => Promise<IControlAttendeeDetailsDTO>;
    onChangeJoinMethod: (value: string) => void;
    status: MeetingAttendeeStatus;
    removeWalkIn: () => void;
    selectedAttendeeFilter?: boolean;
    organizationName?: string;
    handleAttendeeCopy?: () => void;
}

const InfoButton = (props: IProps) => {
    const {
        firstName = '',
        lastName = '',
        markedName,
        markedOrganization,
        handleToggleLate,
        isLate,
        isAttendeeJoined,
        attendeeId,
        openEditAttendeeSubWindow,
        eventTimezone,
        fetchAttendeeData,
        removeWalkIn,
        status,
        selectedAttendeeFilter,
        organizationName,
        handleAttendeeCopy
    } = props;

    const buttonRef = useRef(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [attendeeInfo, setAttendeeInfo] = useState<IAttendeeAdditionalInfo>({
        name: '',
        organizationName: '',
        title: '',
        email: '',
        tel: '',
    });
    const [notes, setNotes] = useState<IAttendeeNote[]>([]);

    const handleOpenModal = () => {
        if (!isModalOpen) {
            fetchAttendee();
        }
        setModalOpen(prev => !prev);
    };

    const fetchAttendee = async () => {
        const data = await fetchAttendeeData(attendeeId);
        setAttendeeInfo({
            name: `${data.firstName} ${data.lastName}`,
            organizationName: data.organizationName,
            title: data.title,
            email: data.email,
            tel: data.phoneNumber,
        });
        setNotes(data.previousMeetingAttendeeNotes);
    };
    const listStyle = selectedAttendeeFilter ? {display: 'flex'} : {border: 'block'};

    return (
        <div
            className={classnames('attendee-button', {
                'attendee-button--active': isModalOpen,
            })}
            ref={buttonRef}
        >
            <div className='attendee-button__wrapper' onClick={handleOpenModal}>
                <span className='attendee-button__label overflow-ellipsis'>
                    {!selectedAttendeeFilter ?
                        <AttendeePresentation
                            firstName={firstName}
                            markedName={markedName}
                            lastName={lastName}
                        /> : <>
                            <div style={listStyle}
                                className='attendees-list-item__label overflow-ellipsis'
                                title={`${firstName} ${lastName} ${organizationName}`}
                            >
                                {markedName ??
                                <Text className='name-limit' message={`${firstName} ${lastName}`} withEllipsis/>}
                                {markedOrganization ??
                                <Text className='organization-limit' message={organizationName} withEllipsis/>}
                            </div>
                        </>}
                </span>
                <div className='attendee-button__chevron' >
                    <ChevronSVG />
                </div>
            </div>
            <Popover
                padding={0}
                isOpen={isModalOpen}
                buttonRef={buttonRef}
                customWidth={buttonRef.current?.getBoundingClientRect().width}
                onClickOutside={() => setModalOpen(false)}
                onClose={() => setModalOpen(false)}
            >
                <OEMultiSelectItem
                    label='Joined late'
                    id='oe.common.joinedLate'
                    value={MeetingAttendeeIssue.LATE}
                    onChange={handleToggleLate}
                    isSelected={isLate}
                    isDisabled={!isAttendeeJoined}
                    withCheckbox
                />
                <AttendeeAdditionalInfo
                    handleCopy={handleAttendeeCopy}
                    attendeeInfo={attendeeInfo}
                    handleClick={openEditAttendeeSubWindow} />
                { status === MeetingAttendeeStatus.WALK_IN && (
                    <div className='remove-button__wrapper'>
                        <DefaultButton
                            text={<FormattedMessage {...REMOVE_TEXT} /> }
                            modifier={['link']}
                            onClick={removeWalkIn}
                        />
                    </div>
                )}
                {!!notes?.length && (
                    <AttendeeNotesSection notes={notes} eventTimezone={eventTimezone} />
                )}
            </Popover>
        </div>
    );
};

export default InfoButton;
